'use client';
import React, { useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import { MOCK_ITEMS } from '@/app/item-master-management/components/ItemMasterContent';
import { STOCK_DATA } from '@/app/stock-positions/components/StockPositionsContent';

interface GIWizardForm {
  site: string;
  bomProject: string;
  purpose: string;
  requester: string;
  urgency: string;
  lineItems: {
    itemId: string;
    requestedQty: number;
    justification: string;
  }[];
}

const SITES = ['Site Alpha', 'Site Beta', 'Site Gamma', 'Warehouse B'];
const BOM_PROJECTS = [
  { code: '', label: 'No BOM link (general issuance)' },
  { code: 'PROJ-2026-011', label: 'PROJ-2026-011 — Foundation Works Block C' },
  { code: 'PROJ-2026-012', label: 'PROJ-2026-012 — Electrical Conduit Install' },
  { code: 'PROJ-2026-013', label: 'PROJ-2026-013 — HVAC Ductwork Phase 2' },
  { code: 'PROJ-2026-014', label: 'PROJ-2026-014 — Steel Structure Tower 2' },
  { code: 'PROJ-2026-015', label: 'PROJ-2026-015 — Plumbing & Drainage Block D' },
];

export default function GIWizard({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, control, watch, getValues, formState: { errors } } = useForm<GIWizardForm>({
    defaultValues: {
      lineItems: [{ itemId: '', requestedQty: 1, justification: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({ control, name: 'lineItems' });
  const lineItems = watch('lineItems');
  const selectedSite = watch('site');

  const totalValue = lineItems.reduce((acc, li) => {
    const item = MOCK_ITEMS.find((i) => i.id === li.itemId);
    return acc + (li.requestedQty || 0) * (item?.unitCost || 0);
  }, 0);

  const onSubmit = async () => {
    setIsSubmitting(true);
    // BACKEND INTEGRATION POINT: POST /api/v1/grgi/issuance
    await new Promise((r) => setTimeout(r, 1200));
    setIsSubmitting(false);
    toast.success('GI request submitted for approval.');
    onClose();
    setStep(1);
  };

  const STEPS = [
    { num: 1, label: 'Request Header' },
    { num: 2, label: 'Line Items' },
    { num: 3, label: 'Review & Submit' },
  ];

  const fieldClass = (hasError: boolean) =>
    `w-full px-3 py-2 border rounded-lg text-sm bg-input text-foreground placeholder:text-muted-foreground/60 outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring transition-all ${
      hasError ? 'border-red-400' : 'border-border'
    }`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New GI Request"
      subtitle="Create a Goods Issuance request for approval"
      size="xl"
      footer={
        <div className="flex items-center justify-between">
          <button
            onClick={() => step > 1 && setStep((s) => s - 1)}
            disabled={step === 1}
            className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted disabled:opacity-40 transition-all"
          >
            <Icon name="ChevronLeftIcon" size={14} />
            Back
          </button>
          <div className="flex items-center gap-2">
            {step < 3 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-600 hover:bg-primary/90 transition-all active:scale-95"
              >
                Next
                <Icon name="ChevronRightIcon" size={14} />
              </button>
            ) : (
              <button
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2 bg-accent text-accent-foreground rounded-lg text-sm font-600 hover:bg-amber-500 transition-all active:scale-95 disabled:opacity-60"
                style={{ minWidth: '160px', justifyContent: 'center' }}
              >
                {isSubmitting ? (
                  <span className="w-4 h-4 border-2 border-accent-foreground/30 border-t-accent-foreground rounded-full animate-spin" />
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={15} />
                    Submit for Approval
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, i) => (
          <React.Fragment key={`gi-wiz-${s.num}`}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-700 transition-all ${
                step > s.num ? 'bg-teal text-white' : step === s.num ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {step > s.num ? <Icon name="CheckIcon" size={13} /> : s.num}
              </div>
              <span className={`text-xs font-500 hidden sm:block ${step === s.num ? 'text-foreground' : 'text-muted-foreground'}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <div className={`flex-1 h-px ${step > s.num ? 'bg-teal' : 'bg-border'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: Header */}
      {step === 1 && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Requester Name <span className="text-red-500">*</span></label>
              <input
                type="text"
                placeholder="Full name of requester"
                className={fieldClass(!!errors.requester)}
                {...register('requester', { required: 'Requester name is required.' })}
              />
              {errors.requester && <p className="mt-1 text-xs text-red-600">{errors.requester.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Issuing Site <span className="text-red-500">*</span></label>
              <select className={fieldClass(!!errors.site)} {...register('site', { required: 'Site is required.' })}>
                <option value="">Select site…</option>
                {SITES.map((s) => <option key={`gi-site-${s}`} value={s}>{s}</option>)}
              </select>
              {errors.site && <p className="mt-1 text-xs text-red-600">{errors.site.message}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-500 text-foreground mb-1.5">Link to BOM Project (Optional)</label>
              <p className="text-xs text-muted-foreground mb-1.5">Linking to a BOM project enables automatic consumption tracking and over-consumption alerts</p>
              <select className={fieldClass(false)} {...register('bomProject')}>
                {BOM_PROJECTS.map((p) => <option key={`gi-bom-${p.code || 'none'}`} value={p.code}>{p.label}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-sm font-500 text-foreground mb-1.5">Purpose / Justification <span className="text-red-500">*</span></label>
              <p className="text-xs text-muted-foreground mb-1.5">Describe the work activity requiring these materials</p>
              <textarea
                rows={3}
                placeholder="e.g. Structural steel works for Tower 2 column erection — Phase 3…"
                className={`${fieldClass(!!errors.purpose)} resize-none`}
                {...register('purpose', { required: 'Purpose is required.', minLength: { value: 10, message: 'Minimum 10 characters.' } })}
              />
              {errors.purpose && <p className="mt-1 text-xs text-red-600">{errors.purpose.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-500 text-foreground mb-1.5">Urgency</label>
              <select className={fieldClass(false)} {...register('urgency')}>
                <option value="NORMAL">Normal — Standard processing</option>
                <option value="URGENT">Urgent — Required within 4 hours</option>
                <option value="CRITICAL">Critical — Site stoppage risk</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Line items */}
      {step === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm font-500 text-foreground">Add items to request</p>
            <button
              type="button"
              onClick={() => append({ itemId: '', requestedQty: 1, justification: '' })}
              className="flex items-center gap-1.5 text-xs text-secondary hover:text-secondary/80 font-600"
            >
              <Icon name="PlusIcon" size={13} />
              Add Line
            </button>
          </div>

          <div className="space-y-3">
            {fields.map((field, idx) => {
              const selectedItemId = lineItems[idx]?.itemId;
              const selectedItem = MOCK_ITEMS.find((i) => i.id === selectedItemId);
              const stockForSite = selectedSite
                ? STOCK_DATA.find((s) => s.itemCode === selectedItem?.itemCode && s.site === selectedSite)
                : null;
              const available = stockForSite?.available ?? 0;
              const requested = lineItems[idx]?.requestedQty ?? 0;
              const shortage = requested > available;

              return (
                <div key={field.id} className={`border rounded-lg p-4 relative ${shortage && selectedItemId ? 'border-amber-300 bg-amber-50/30' : 'border-border bg-muted/20'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-600 text-muted-foreground">Line {idx + 1}</span>
                    {fields.length > 1 && (
                      <button type="button" onClick={() => remove(idx)} className="p-1 hover:bg-red-50 text-red-400 rounded transition-colors">
                        <Icon name="TrashIcon" size={13} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-500 text-foreground mb-1">Item <span className="text-red-500">*</span></label>
                      <select className={fieldClass(false)} {...register(`lineItems.${idx}.itemId`)}>
                        <option value="">Select item…</option>
                        {MOCK_ITEMS.filter((i) => i.isActive).map((item) => (
                          <option key={`gi-li-${idx}-${item.id}`} value={item.id}>{item.itemCode} — {item.itemName}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-500 text-foreground mb-1">Requested Qty <span className="text-red-500">*</span></label>
                      <input type="number" min="1" placeholder="0" className={fieldClass(false)} {...register(`lineItems.${idx}.requestedQty`, { valueAsNumber: true })} />
                    </div>
                  </div>

                  {/* Availability check */}
                  {selectedItem && selectedSite && (
                    <div className={`mt-3 flex items-center gap-3 px-3 py-2 rounded-md text-xs ${shortage ? 'bg-amber-100 text-amber-800' : 'bg-teal/5 text-teal'}`}>
                      <Icon name={shortage ? 'ExclamationTriangleIcon' : 'CheckCircleIcon'} size={13} />
                      <span>
                        Available at {selectedSite}: <strong>{available} {selectedItem.uom}</strong>
                        {shortage && ` — Shortage of ${requested - available} ${selectedItem.uom}. Manager override required.`}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-end gap-4 px-4 py-3 bg-primary/5 border border-primary/15 rounded-lg">
            <span className="text-sm text-muted-foreground">Estimated Total:</span>
            <span className="text-lg font-700 text-foreground tabular-nums">
              € {totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
            </span>
          </div>
        </div>
      )}

      {/* Step 3: Review */}
      {step === 3 && (
        <div className="space-y-5">
          <div className="bg-secondary/5 border border-secondary/20 rounded-lg px-4 py-3 flex gap-3">
            <Icon name="InformationCircleIcon" size={16} className="text-secondary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground leading-relaxed">
              Submitting this request will notify the Store Manager for approval. You will receive a notification once approved or rejected. Physical issuance requires recipient signature at the site gate.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3">Request Summary</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { label: 'Requester', value: getValues('requester') || '—' },
                { label: 'Site', value: getValues('site') || '—' },
                { label: 'BOM Project', value: getValues('bomProject') || 'None' },
                { label: 'Total Value', value: `€ ${totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}` },
                { label: 'Line Items', value: `${fields.length} item${fields.length !== 1 ? 's' : ''}` },
                { label: 'Urgency', value: getValues('urgency') || 'Normal' },
              ].map((m) => (
                <div key={`gi-rev-${m.label}`} className="bg-muted/40 rounded-lg px-3 py-2.5">
                  <p className="text-[10px] font-600 uppercase tracking-wider text-muted-foreground">{m.label}</p>
                  <p className="text-sm font-500 text-foreground mt-0.5">{m.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-2">Purpose</h4>
            <p className="text-sm text-foreground bg-muted/40 rounded-lg px-3 py-2.5">{getValues('purpose') || '—'}</p>
          </div>

          <div>
            <h4 className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3">Requested Items ({fields.length})</h4>
            <div className="border border-border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    {['Item', 'Qty', 'UOM', 'Unit Cost', 'Line Total'].map((h) => (
                      <th key={`gi-rev-th-${h}`} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-2">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lineItems.map((li, idx) => {
                    const item = MOCK_ITEMS.find((i) => i.id === li.itemId);
                    const lineTotal = (li.requestedQty || 0) * (item?.unitCost || 0);
                    return (
                      <tr key={`gi-rev-li-${idx}`} className="hover:bg-muted/30 transition-colors">
                        <td className="px-3 py-2.5">
                          <p className="text-xs font-600 text-foreground">{item?.itemName ?? '—'}</p>
                          <p className="text-[11px] font-mono-data text-muted-foreground">{item?.itemCode ?? ''}</p>
                        </td>
                        <td className="px-3 py-2.5 text-xs tabular-nums font-500 text-foreground">{li.requestedQty}</td>
                        <td className="px-3 py-2.5 text-xs text-muted-foreground">{item?.uom ?? ''}</td>
                        <td className="px-3 py-2.5 text-xs tabular-nums text-foreground">€ {(item?.unitCost ?? 0).toFixed(2)}</td>
                        <td className="px-3 py-2.5 text-xs tabular-nums font-600 text-foreground">€ {lineTotal.toLocaleString('de-DE', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-border bg-muted/30">
                    <td colSpan={4} className="px-3 py-2.5 text-xs font-600 text-right text-muted-foreground">Total</td>
                    <td className="px-3 py-2.5 text-sm font-700 text-foreground tabular-nums">
                      € {totalValue.toLocaleString('de-DE', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}